import React from 'react';
import SlidePanel from '../shared/SlidePanel';
import VoucherCard from './VoucherCard';
import CopyLinkButton from '../shared/shareLinks';

// VoucherDetailPanel — read-only slide-out shown when the salon clicks a
// voucher row. Mirrors DesignPreviewPanel / SalesDetailPanel: a card-preview
// hero + stat strip + read-only sections, with a single "Edit voucher"
// action in the footer that hands off to the editor. Clicking a row used to
// jump straight into edit; this gives a calm overview first.

const STATUS_CONFIG = {
  active: { label: 'Active', bg: '#d1fae5', color: '#059669' },
  draft:  { label: 'Draft',  bg: '#f3f4f6', color: '#6b7280' },
};

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 2.5l2.5 2.5M2 12l.5-2.5L9 3l2 2-6.5 6.5L2 12z" />
  </svg>
);

// Build a preview voucher object from a design so the hero <VoucherCard>
// renders that design's look. Same shape DesignPreviewPanel uses; custom
// designs pass their full saved config through to CustomCardScene.
const buildPreviewVoucher = (design, value) => {
  if (!design) return null;
  if (design.isCustom) {
    return {
      name: 'Gift Card',
      template: 'signature',
      theme: design.theme,
      value,
      promo: null,
      customDesign: design.config,
    };
  }
  return {
    name: 'Gift Card',
    template: design.occasion || 'signature',
    theme: design.theme,
    value,
    promo: null,
  };
};

const VoucherDetailPanel = ({ voucher, designs = [], currency = '£', salonName, onClose, onEdit }) => {
  if (!voucher) return null;

  const status = STATUS_CONFIG[voucher.status] || STATUS_CONFIG.active;
  const denominations = voucher.denominations || [];
  const minDenom = denominations.length ? Math.min(...denominations) : voucher.value;
  const promoActive = voucher.promo != null && voucher.promo < (voucher.value ?? minDenom);

  // Resolve the designs this voucher offers. A populated designIds[] is a
  // curated subset; an empty array means "every available design".
  const ids = voucher.designIds || [];
  const assignedDesigns = ids.length
    ? ids.map((id) => designs.find((d) => d.id === id)).filter(Boolean)
    : designs.filter((d) => d.available);
  const heroValue = voucher.value ?? minDenom ?? 50;
  // Cap the visible thumbnails so an "all available" voucher (could be 25+)
  // doesn't render a wall of cards; a "+N" tile covers the overflow.
  const MAX_THUMBS = 10;
  const shownDesigns = assignedDesigns.slice(0, MAX_THUMBS);
  const extraDesigns = assignedDesigns.length - shownDesigns.length;

  const footer = (
    <button
      type="button"
      className="sp-btn-primary"
      onClick={() => { if (onEdit) onEdit(voucher); }}
    >
      Edit voucher
    </button>
  );

  // Copy link lives in the header next to the close button (matching the service
  // and product panels); the status pill sits just left of it.
  const headerRight = (
    <>
      <span
        className="v2-sales-detail-status-pill"
        style={{ background: status.bg, color: status.color }}
      >
        {status.label}
      </span>
      <CopyLinkButton type="voucher" id={voucher.id} title="Copy voucher link" />
    </>
  );

  return (
    <SlidePanel
      title={voucher.name || 'Voucher'}
      onClose={onClose}
      headerRight={headerRight}
      footer={footer}
      width={520}
      className="voucher-v2 v2-voucher-detail"
    >
      {/* Designs gallery — the voucher's design(s) as a scrollable strip of
          card thumbnails, so a curated multi-design voucher is scannable at
          a glance instead of a single arbitrary hero. */}
      {assignedDesigns.length > 0 && (
        <div className="v2-vd-gallery">
          <div className="v2-vd-design-strip">
            {shownDesigns.map((d) => (
              <div key={d.id} className="v2-vd-design-thumb">
                <VoucherCard
                  voucher={buildPreviewVoucher(d, heroValue)}
                  aesthetic={d.aesthetic}
                  salonName={salonName}
                  currency={currency}
                  showPromo={false}
                />
              </div>
            ))}
            {extraDesigns > 0 && (
              <div className="v2-vd-design-more">+{extraDesigns}</div>
            )}
          </div>
          <p className="v2-vd-preview-caption">
            {ids.length === 0
              ? `All ${assignedDesigns.length} available designs`
              : `${assignedDesigns.length} design${assignedDesigns.length === 1 ? '' : 's'} on this voucher`}
          </p>
        </div>
      )}

      {/* Stat strip — quick read of the product's shape. */}
      <div className="v2-pp-stats v2-vd-stats">
        <div className="v2-pp-stat">
          <div className="v2-pp-stat-num">{currency}{minDenom}</div>
          <div className="v2-pp-stat-label">from</div>
        </div>
        <div className="v2-pp-stat">
          <div className="v2-pp-stat-num">{denominations.length || '—'}</div>
          <div className="v2-pp-stat-label">denominations</div>
        </div>
        <div className="v2-pp-stat">
          <div className="v2-pp-stat-num">{voucher.sold || 0}</div>
          <div className="v2-pp-stat-label">sold</div>
        </div>
      </div>

      {/* Denominations */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Denominations</p>
        <div className="v2-denoms">
          {denominations.map((d) => (
            <span key={d} className="v2-denom-chip">{currency}{d}</span>
          ))}
          {voucher.customAmount && (
            <span className="v2-denom-chip v2-denom-chip--custom">Custom</span>
          )}
          {denominations.length === 0 && !voucher.customAmount && (
            <span className="v2-pp-help">No denominations set.</span>
          )}
        </div>
      </div>

      {/* Pricing */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Pricing</p>
        <div className="v2-sd-rows">
          <div className="v2-sd-row">
            <span>Default value</span>
            <strong>{currency}{voucher.value ?? minDenom}</strong>
          </div>
          {promoActive && (
            <div className="v2-sd-row">
              <span>Promo price</span>
              <strong>
                {currency}{voucher.promo}{' '}
                <span className="v2-sd-row-meta">promo</span>
              </strong>
            </div>
          )}
          {voucher.customAmount && (
            <div className="v2-sd-row">
              <span>Custom amount</span>
              <strong>
                {currency}{voucher.customMin ?? 0}
                {voucher.customMax ? ` to ${currency}${voucher.customMax}` : '+'}
              </strong>
            </div>
          )}
        </div>
      </div>

      {/* Availability — status + channels, read-only mirror of the editor. */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Availability</p>
        <div className="v2-sd-rows">
          <div className="v2-sd-row">
            <span>Status</span>
            <strong>{status.label}</strong>
          </div>
          <div className="v2-sd-row">
            <span>Online booking page</span>
            <strong>{voucher.online ? 'Listed' : 'Off'}</strong>
          </div>
          <div className="v2-sd-row">
            <span>In-store POS</span>
            <strong>{voucher.pos ? 'Sellable' : 'Off'}</strong>
          </div>
        </div>
      </div>

      {/* Expiry */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Expiry</p>
        <div className="v2-sd-rows">
          <div className="v2-sd-row">
            <span>Valid for</span>
            <strong>{voucher.expiry || 'Never expires'}</strong>
          </div>
        </div>
      </div>

      {/* Terms */}
      {voucher.tc && (
        <div className="v2-pp-section">
          <p className="beu-section-label">Terms &amp; conditions</p>
          <p className="v2-vd-terms">{voucher.tc}</p>
        </div>
      )}
    </SlidePanel>
  );
};

export default VoucherDetailPanel;
